import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  CheckCircleIcon,
  CreditCardIcon,
  GlobeAltIcon,
  SparklesIcon,
  BuildingOffice2Icon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { useAppStore } from '../store/useAppStore'
import { supabase } from '../lib/supabase'
import { clsx } from 'clsx'

const db = supabase as any

declare global {
  interface Window {
    PaystackPop: any
  }
}

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

const currencies = [
  { code: 'USD', symbol: '$', label: 'US Dollar', rate: 1 },
  { code: 'NGN', symbol: '₦', label: 'Nigerian Naira', rate: 1550 },
  { code: 'GBP', symbol: '£', label: 'British Pound', rate: 0.79 },
  { code: 'EUR', symbol: '€', label: 'Euro', rate: 0.92 },
  { code: 'GHS', symbol: 'GH₵', label: 'Ghanaian Cedi', rate: 15.2 },
  { code: 'ZAR', symbol: 'R', label: 'South African Rand', rate: 18.5 },
  { code: 'KES', symbol: 'KSh', label: 'Kenyan Shilling', rate: 153 },
]

interface PlanFeature {
  name: string
  starter: boolean | string
  professional: boolean | string
  enterprise: boolean | string
}

const features: PlanFeature[] = [
  { name: 'Dashboard & Analytics', starter: true, professional: true, enterprise: true },
  { name: 'AI-Powered Insights', starter: 'Basic', professional: 'Advanced', enterprise: 'Unlimited' },
  { name: 'Team Members', starter: '1 user', professional: '5 users', enterprise: 'Unlimited' },
  { name: 'Data Sources', starter: '2 sources', professional: '10 sources', enterprise: 'Unlimited' },
  { name: 'Marketing Channels', starter: 'Email only', professional: 'All channels', enterprise: 'All channels + API' },
  { name: 'CRM Contacts', starter: '100', professional: '5,000', enterprise: 'Unlimited' },
  { name: 'AI Chat Agent', starter: false, professional: true, enterprise: true },
  { name: 'Voice Agent', starter: false, professional: false, enterprise: true },
  { name: 'Vulnerability Scans', starter: '1/month', professional: '10/month', enterprise: 'Unlimited' },
  { name: 'Mobile App Builder', starter: false, professional: true, enterprise: true },
  { name: 'Google Maps Optimization', starter: '1 listing', professional: '5 listings', enterprise: 'Unlimited' },
  { name: 'White Label', starter: false, professional: false, enterprise: true },
  { name: 'API Access', starter: false, professional: true, enterprise: true },
  { name: 'Priority Support', starter: false, professional: true, enterprise: true },
  { name: 'Dedicated Account Manager', starter: false, professional: false, enterprise: true },
]

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    description: 'Perfect for small businesses getting started',
    monthlyPriceUSD: 29,
    yearlyPriceUSD: 290,
    icon: RocketLaunchIcon,
    color: 'primary',
    popular: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'For growing businesses that need more power',
    monthlyPriceUSD: 79,
    yearlyPriceUSD: 790,
    icon: SparklesIcon,
    color: 'primary',
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'For large organizations with custom needs',
    monthlyPriceUSD: 199,
    yearlyPriceUSD: 1990,
    icon: BuildingOffice2Icon,
    color: 'primary',
    popular: false,
  },
]

export const Pricing = () => {
  const { user } = useAppStore()
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const [selectedCurrency, setSelectedCurrency] = useState('USD')
  const [processingPlan, setProcessingPlan] = useState<string | null>(null)

  const currency = currencies.find((c) => c.code === selectedCurrency) || currencies[0]

  const getPrice = (usdPrice: number) => {
    const converted = usdPrice * currency.rate
    if (currency.code === 'NGN' || currency.code === 'KES') {
      return Math.round(converted / 10) * 10
    }
    return Math.round(converted)
  }

  const formatPrice = (usdPrice: number) => {
    const price = getPrice(usdPrice)
    if (currency.code === 'NGN' || currency.code === 'GHS' || currency.code === 'KES') {
      return `${currency.symbol}${price.toLocaleString()}`
    }
    return `${currency.symbol}${price.toLocaleString()}`
  }

  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://js.paystack.co/v1/inline.js'
    script.async = true
    document.body.appendChild(script)
    return () => { document.body.removeChild(script) }
  }, [])

  const handleSubscribe = async (plan: typeof plans[0]) => {
    if (!user) return

    setProcessingPlan(plan.id)

    const priceUSD = billingCycle === 'monthly' ? plan.monthlyPriceUSD : plan.yearlyPriceUSD
    const amountInKobo = Math.round(priceUSD * currency.rate * 100)

    const paystack = new window.PaystackPop()
    paystack.newTransaction({
      key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || '',
      email: user.email,
      amount: amountInKobo,
      currency: currency.code,
      metadata: {
        custom_fields: [
          { display_name: 'Plan', variable_name: 'plan', value: plan.id },
          { display_name: 'Billing Cycle', variable_name: 'billing_cycle', value: billingCycle },
          { display_name: 'User ID', variable_name: 'user_id', value: user.id },
        ],
      },
      callback: async (response: any) => {
        try {
          await db.from('subscriptions').upsert({
            user_id: user.id,
            plan: plan.id as any,
            billing_cycle: billingCycle,
            currency: currency.code,
            amount: priceUSD,
            paystack_ref: response.reference,
            status: 'active',
            current_period_start: new Date().toISOString(),
            current_period_end: new Date(Date.now() + (billingCycle === 'monthly' ? 30 : 365) * 24 * 60 * 60 * 1000).toISOString(),
          })

          await db.from('payment_history').insert({
            user_id: user.id,
            amount: priceUSD,
            currency: currency.code,
            paystack_ref: response.reference,
            plan: plan.id,
            status: 'success',
          })

          window.location.reload()
        } catch (err) {
          console.error('Subscription save error:', err)
        }
        setProcessingPlan(null)
      },
      onClose: () => {
        setProcessingPlan(null)
      },
    })
  }

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
      <motion.div variants={item} className="text-center max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-metallic-900">Choose Your Plan</h1>
        <p className="text-metallic-500 mt-1">Scale as you grow. No hidden fees. Cancel anytime.</p>
      </motion.div>

      {/* Controls */}
      <motion.div variants={item} className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <div className="flex bg-metallic-100 rounded-lg p-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={clsx('px-4 py-2 text-sm font-medium rounded-md transition-colors', billingCycle === 'monthly' ? 'bg-white text-metallic-900 shadow-sm' : 'text-metallic-500')}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={clsx('px-4 py-2 text-sm font-medium rounded-md transition-colors', billingCycle === 'yearly' ? 'bg-white text-metallic-900 shadow-sm' : 'text-metallic-500')}
          >
            Yearly <span className="text-success-500 text-xs">Save 17%</span>
          </button>
        </div>

        <div className="relative">
          <GlobeAltIcon className="w-4 h-4 text-metallic-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <select
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
            className="pl-9 pr-8 py-2 bg-white border border-metallic-200 rounded-lg text-sm text-metallic-700 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {currencies.map((c) => (
              <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.label}</option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* Plans */}
      <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {plans.map((plan) => {
          const monthlyPrice = billingCycle === 'monthly' ? plan.monthlyPriceUSD : plan.yearlyPriceUSD / 12
          return (
            <Card key={plan.id} className={clsx('relative flex flex-col', plan.popular && 'ring-2 ring-primary-500 shadow-metallic-lg')}>
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge variant="primary">Most Popular</Badge>
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <plan.icon className="w-10 h-10 text-primary-600 mb-4" />
                <h3 className="text-xl font-bold text-metallic-900">{plan.name}</h3>
                <p className="text-sm text-metallic-500 mt-1 mb-6">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-extrabold text-metallic-900">{formatPrice(monthlyPrice)}</span>
                  <span className="text-metallic-500 text-sm">/month</span>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-success-500 mt-1">Billed {formatPrice(plan.yearlyPriceUSD)} annually</p>
                  )}
                </div>
                <Button
                  variant={plan.popular ? 'primary' : 'secondary'}
                  className="w-full"
                  isLoading={processingPlan === plan.id}
                  onClick={() => handleSubscribe(plan)}
                >
                  {user?.plan === plan.id ? 'Current Plan' : 'Subscribe Now'}
                </Button>
              </div>
            </Card>
          )
        })}
      </motion.div>

      {/* Feature Comparison */}
      <motion.div variants={item} className="max-w-5xl mx-auto">
        <Card>
          <h3 className="text-lg font-semibold text-metallic-900 mb-6 text-center">Compare All Features</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-metallic-500 border-b border-metallic-100">
                  <th className="pb-3 font-medium w-1/2">Feature</th>
                  <th className="pb-3 font-medium text-center">Starter</th>
                  <th className="pb-3 font-medium text-center">Professional</th>
                  <th className="pb-3 font-medium text-center">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-metallic-100">
                {features.map((f) => (
                  <tr key={f.name} className="hover:bg-metallic-50">
                    <td className="py-3 text-sm text-metallic-700">{f.name}</td>
                    {(['starter', 'professional', 'enterprise'] as const).map((plan) => (
                      <td key={plan} className="py-3 text-center">
                        {typeof f[plan] === 'boolean' ? (
                          f[plan] ? (
                            <CheckCircleIcon className="w-5 h-5 text-success-500 mx-auto" />
                          ) : (
                            <span className="text-metallic-300">—</span>
                          )
                        ) : (
                          <span className="text-sm text-metallic-700 font-medium">{f[plan] as string}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </motion.div>

      {/* Trust */}
      <motion.div variants={item} className="text-center text-sm text-metallic-400 space-y-1 pb-8">
        <p className="flex items-center justify-center gap-2">
          <CreditCardIcon className="w-4 h-4" /> Secure payments via Paystack
        </p>
        <p>All prices include applicable taxes. 14-day money-back guarantee.</p>
      </motion.div>
    </motion.div>
  )
}
